import { brandPalette } from "../../../constants/brandPalette";
import {
  Box,
  Button,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  adminModalBodyProps,
  adminModalContentProps,
  adminModalHeaderProps,
} from "../../../components/Models/admin-modal-theme";
import {
  clearCorrectedQuestions,
  getCorrectedQuestionsRequest,
} from "../../../modules/admin-modules/student-exams/Actions";
import MathRichText from "./math-rich-text";
import { getQuestionData } from "./question-item";

const QUESTION_IMAGE_BASE = "https://edutopiaonline.com/api";

const PreviewCorrectionModal = ({ isOpen, onClose, studentExam }) => {
  const dispatch = useDispatch();
  const correctedQuestions = useSelector((state) => state.adminStudentExams.correctedQuestions);
  const isCorrectedLoading = useSelector((state) => state.adminStudentExams.isCorrectedLoading);

  useEffect(() => {
    if (!isOpen || !studentExam?.id) return;
    dispatch(getCorrectedQuestionsRequest(studentExam.id));
  }, [isOpen, studentExam?.id, dispatch]);

  const handleClose = useCallback(() => {
    dispatch(clearCorrectedQuestions());
    onClose();
  }, [dispatch, onClose]);

  const studentLabel = useMemo(() => {
    const name = studentExam?.student?.name;
    const code = studentExam?.student?.user_name;
    if (name && code) return `${name} (${code})`;
    return name || code || "Student";
  }, [studentExam]);

  const totalAwarded = useMemo(
    () =>
      (correctedQuestions || []).reduce((sum, item) => sum + (Number(item?.mark) || 0), 0),
    [correctedQuestions]
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent
        {...adminModalContentProps}
        maxH="90vh"
        bg="var(--surface-raised)"
        color="var(--text-primary)"
      >
        <ModalHeader {...adminModalHeaderProps}>
          Correction preview — {studentLabel}
        </ModalHeader>
        <ModalCloseButton color="var(--text-primary)" />
        <ModalBody {...adminModalBodyProps}>
          {isCorrectedLoading ? (
            <VStack py={12}>
              <Spinner size="lg" color={brandPalette.primary} />
              <Text fontSize="sm" color="var(--text-muted)">
                Loading correction…
              </Text>
            </VStack>
          ) : !correctedQuestions?.length ? (
            <Text fontSize="sm" color="var(--text-muted)" py={8} textAlign="center">
              No correction data found for this submission.
            </Text>
          ) : (
            <Stack spacing={8}>
              <Box
                p={4}
                borderRadius="xl"
                bg="var(--surface-muted)"
                borderWidth="1px"
                borderColor="var(--border-subtle)"
              >
                <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" textTransform="uppercase" mb={1}>
                  Total score
                </Text>
                <Text fontSize="2xl" fontWeight="800" color={brandPalette.primary}>
                  {totalAwarded}
                </Text>
              </Box>

              {correctedQuestions.map((item, index) => {
                const question = getQuestionData(item);
                const maxMark = Number(question?.mark) || 0;
                const awarded = Number(item?.mark) ?? 0;

                return (
                  <Box
                    key={item?.id ?? item?.question_id ?? index}
                    p={5}
                    borderWidth="1px"
                    borderColor="var(--border-subtle)"
                    borderRadius="xl"
                    bg="var(--surface-muted)"
                  >
                    <Text
                      fontSize="xs"
                      fontWeight="800"
                      color="var(--text-muted)"
                      textTransform="uppercase"
                      letterSpacing="0.1em"
                      mb={3}
                    >
                      Question {index + 1} · {awarded} / {maxMark} marks
                    </Text>

                    {question?.comprehension ? (
                      <Box mb={4}>
                        <Text fontSize="xs" fontWeight="700" color="var(--text-muted)" mb={1}>
                          Passage
                        </Text>
                        <Text fontSize="sm" color="var(--text-primary)" whiteSpace="pre-wrap">
                          {question.comprehension}
                        </Text>
                      </Box>
                    ) : null}

                    {question?.question ? (
                      <Box
                        mb={4}
                        fontSize="md"
                        color="var(--text-primary)"
                        sx={{
                          "& p, & span, & div, & li": { color: "inherit" },
                        }}
                        dangerouslySetInnerHTML={{ __html: question.question }}
                      />
                    ) : null}

                    {question?.image ? (
                      <Image
                        src={`${QUESTION_IMAGE_BASE}/${question.image}`}
                        alt={`Question ${index + 1}`}
                        maxW="100%"
                        maxH="280px"
                        objectFit="contain"
                        borderRadius="lg"
                        borderWidth="1px"
                        borderColor="var(--border-subtle)"
                        mb={4}
                      />
                    ) : null}

                    <Stack spacing={3}>
                      <Box>
                        <Text fontSize="xs" fontWeight="700" color="var(--text-muted)" mb={1}>
                          Mark awarded
                        </Text>
                        <Text fontSize="md" fontWeight="800" color="var(--text-primary)">
                          {awarded} / {maxMark}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" fontWeight="700" color="var(--text-muted)" mb={1}>
                          Comment
                        </Text>
                        <Box
                          fontSize="sm"
                          color="var(--text-primary)"
                          p={3}
                          borderRadius="lg"
                          bg="var(--surface-raised)"
                          borderWidth="1px"
                          borderColor="var(--border-subtle)"
                        >
                          {item?.comment?.trim() ? (
                            <MathRichText>{item.comment}</MathRichText>
                          ) : (
                            "—"
                          )}
                        </Box>
                      </Box>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={handleClose} fontWeight="700">
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PreviewCorrectionModal;
